import { gameFilterValueFromConceptKey } from "../../game-filter-param.js";
import {
  GAME_ABANDONED_COLOR,
  gameColorFromConceptKey,
  gameDisplayLabel,
  lighterGameColor,
} from "../../lib/gamePalette.js";
import type { EngagementData, OpenRawEventsHandler } from "../../types.js";
import { ChartCard } from "../ChartCard.js";
import { BarChart } from "../charts/BarChart.js";

const GAMES_COMPLETED_LEGEND = [
  { label: "First finish (game colour)", fill: "#5aa2ff" },
  { label: "Repeat (lighter)", fill: "#9ec5ff" },
  { label: "Abandoned", fill: GAME_ABANDONED_COLOR },
] as const;

const GAMES_COMPLETED_CRITERIA = `Source: device_events where event_name in ('game_completed','game_abandoned'), grouped by canonical playable key (\`/games/{slug}\` from resolved route or slugifiable game_name — same as Minutes per Start / Avg Load).

Above baseline (stacked, game colour): **game_completed** — lower = first completion in range for that device + key (row_number 1); upper = repeat completions (2nd+).

Below baseline (muted slate): **game_abandoned** count for the same key.

Bar click: Raw Events → game_completed (above) or game_abandoned (below) with game_url filter for that route.`;

interface GamesCompletedByGameCardProps {
  gamesCompletedByGame: EngagementData["gamesCompletedByGame"];
  totalGamesCompleted: number;
  totalGamesAbandoned: number;
  onOpenRawEvents: OpenRawEventsHandler;
  showCriteria?: boolean;
}

export function GamesCompletedByGameCard({
  gamesCompletedByGame,
  totalGamesCompleted,
  totalGamesAbandoned,
  onOpenRawEvents,
  showCriteria = true,
}: GamesCompletedByGameCardProps) {
  const rows = [...gamesCompletedByGame].filter(
    (x) => x.count > 0 || x.abandonedCount > 0,
  );

  return (
    <ChartCard
      title="Games Completed by Game"
      criteria={showCriteria ? GAMES_COMPLETED_CRITERIA : undefined}
      note="Stacked above baseline: finishes (first then repeat, game colour). Below baseline in slate: abandoned."
      titleRight={
        <span className="shrink-0 text-[12px] font-medium tabular-nums text-text-secondary">
          {totalGamesCompleted.toLocaleString()} done
          {totalGamesAbandoned > 0 ? (
            <span className="text-text-secondary"> · {totalGamesAbandoned.toLocaleString()} abandoned</span>
          ) : null}
        </span>
      }
    >
      <BarChart
        maxBarWidth={52}
        legend={[...GAMES_COMPLETED_LEGEND]}
        data={rows.map((x) => {
          const base = gameColorFromConceptKey(x.game);
          const repeat = lighterGameColor(base);
          return {
            label: x.game,
            value: x.count,
            valueLabel:
              x.count > 0 && x.abandonedCount > 0
                ? `${x.count} / −${x.abandonedCount}`
                : x.abandonedCount > 0 && x.count === 0
                  ? `−${x.abandonedCount}`
                  : String(x.count),
            stack:
              x.count > 0
                ? [
                    { value: x.firstCount, fill: base, label: "First" },
                    { value: x.repeatCount, fill: repeat, label: "Repeat" },
                  ]
                : undefined,
            stackBelow:
              x.abandonedCount > 0
                ? [{ value: x.abandonedCount, fill: GAME_ABANDONED_COLOR, label: "Abandoned" }]
                : undefined,
          };
        })}
        barTooltip={(item, _i, segmentIndex) => {
          const row = rows.find((g) => g.game === item.label);
          const name = gameDisplayLabel(item.label);
          const first = row?.firstCount ?? 0;
          const rep = row?.repeatCount ?? 0;
          const abandoned = row?.abandonedCount ?? 0;
          const completedSegs = row && row.count > 0 ? 2 : 0;
          if (segmentIndex === 0) {
            return `${name} · First in range: ${first} — click: Raw Events → game_completed. Middle-click / Ctrl/Cmd+click: new tab.`;
          }
          if (segmentIndex === 1) {
            return `${name} · Repeat: ${rep} — click: Raw Events → game_completed. Middle-click / Ctrl/Cmd+click: new tab.`;
          }
          if (segmentIndex !== undefined && segmentIndex >= completedSegs) {
            return `${name} · Abandoned: ${abandoned} — click: Raw Events → game_abandoned. Middle-click / Ctrl/Cmd+click: new tab.`;
          }
          return `${name}: ${first} first, ${rep} repeat${abandoned > 0 ? `, ${abandoned} abandoned` : ""} — click: Raw Events. Middle-click / Ctrl/Cmd+click: new tab.`;
        }}
        onBarClick={(item, _i, segIdx, e) => {
          const row = rows.find((g) => g.game === item.label);
          const completedSegs = row && row.count > 0 ? 2 : 0;
          const isAbandoned = segIdx !== undefined && segIdx >= completedSegs;
          onOpenRawEvents(
            {
              eventName: isAbandoned ? "game_abandoned" : "game_completed",
              gameUrl: gameFilterValueFromConceptKey(item.label),
            },
            e,
          );
        }}
      />
    </ChartCard>
  );
}
